/**
 * `NavPanes` (1.I-2 deliverables 13 to 15), the root `Content`: the routed page, the split pane
 * beside it (or the two as tabs on a narrow window), and the palette with its header trigger.
 * With `shell` off it renders its children only. The routed page keeps one place in the tree
 * whether or not pane 2 is open, so opening the split never remounts it.
 */
import { isApplePlatform, LAYOUT, SeparatorHandle } from '@codex/ui';
import { useMatches } from '@tanstack/react-router';
import {
  type KeyboardEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { ShellHeaderItems } from '../shell/header-slot.js';
import { type NavApi, useNavApi } from './nav-context.js';
import { setActive } from './nav-state.js';
import { onPaletteKey } from './palette-keys.js';
import { LazyPalette } from './palette-lazy.js';
import { PaletteTrigger } from './palette-trigger.js';
import { paneControls } from './pane-controls.js';
import { currentEntry } from './pane-history.js';
import { LazyPaneDocument } from './pane-lazy.js';
import { PeekHeader, usePaneTitle } from './peek-header.js';
import {
  clampFirstWidth,
  firstPaneMax,
  focusIdAfterSwap,
  HANDLE_WIDTH,
  splitLayout,
} from './split-model.js';

const DOC_ROUTE = '/doc/$id';

/** The id of the document the routed page shows, null on any other route. */
function usePageDocId(): string | null {
  const matches = useMatches();
  const last = matches[matches.length - 1];
  if (!last || last.routeId !== DOC_ROUTE) return null;
  return (last.params as { id?: string }).id ?? null;
}

/** The viewport and the content region's width, measured on the region's element. */
function useRoom(anchor: React.RefObject<HTMLElement | null>): {
  viewport: number;
  region: number;
} {
  const [room, setRoom] = useState({ viewport: window.innerWidth, region: 0 });
  useEffect(() => {
    const region = anchor.current?.parentElement;
    const measure = () =>
      setRoom((prev) => {
        const next = {
          viewport: window.innerWidth,
          region: Math.round(region?.getBoundingClientRect().width ?? 0),
        };
        return prev.viewport === next.viewport && prev.region === next.region ? prev : next;
      });
    measure();
    window.addEventListener('resize', measure);
    const observer = region ? new ResizeObserver(measure) : null;
    if (region) observer?.observe(region);
    return () => {
      window.removeEventListener('resize', measure);
      observer?.disconnect();
    };
  }, [anchor]);
  return room;
}

/**
 * How long pane 1's offset is held after the split opens or closes: at least this many frames
 * and milliseconds, and until the region and the panes have not resized for a while, but never
 * longer than the cap.
 */
const KEEP_SCROLL_FRAMES = 8;
const KEEP_SCROLL_MS = 400;
const KEEP_SCROLL_QUIET_FRAMES = 3;
const KEEP_SCROLL_QUIET_MS = 150;
const KEEP_SCROLL_MAX_MS = 2500;
/** Input that is the reader scrolling: it ends the putting back at once. */
const READER_INPUT = ['wheel', 'touchstart', 'keydown', 'pointerdown'] as const;

/**
 * Pane 1 keeps the content region as its scroller, so opening or closing the split must leave its
 * offset where it was (DECISIONS 441). Pane 1 narrows or widens with the toggle and its page
 * settles over the next frames (the document frame docks or undocks its rail once its
 * ResizeObserver has run), and each of those reflows lets the browser's scroll anchoring move
 * the offset; focus or a scrollIntoView in the new layout can move it too. The offset to keep is
 * read from the region while the toggle renders, before the DOM changes (a scroll event lags a
 * frame behind a scrollTop write, so it cannot be the source). For a hold after the toggle the
 * region's anchoring is off and that offset is put back on each frame; the hold lasts until the
 * layout has stopped resizing, within a minimum and a cap. Anything the reader does to scroll
 * ends the putting back at once (the anchoring stays off until the hold ends, so that no late
 * reflow moves what the reader chose). `shown` is false while pane 1 is a hidden tab.
 */
function useKeepPaneOneScroll(
  anchor: React.RefObject<HTMLElement | null>,
  open: boolean,
  shown: boolean,
): void {
  const holding = useRef<(() => void) | null>(null);
  const previous = useRef({ open, shown });
  const before = useRef<number | null>(null);

  // During render the DOM still has the layout from before the toggle.
  const committed = previous.current;
  if (committed.open !== open && committed.shown && shown && before.current === null) {
    const region = anchor.current?.parentElement;
    if (region) before.current = region.scrollTop;
  }

  useEffect(() => () => holding.current?.(), []);

  useLayoutEffect(() => {
    const was = previous.current;
    previous.current = { open, shown };
    const snapshot = before.current;
    before.current = null;
    if (was.open === open || !was.shown || !shown) return;
    const scroller = anchor.current?.parentElement;
    if (!scroller) return;
    holding.current?.();
    const anchoring = scroller.style.overflowAnchor;
    scroller.style.overflowAnchor = 'none';
    const target = snapshot ?? scroller.scrollTop;
    let restoring = true;
    const put = () => {
      if (restoring && scroller.scrollTop !== target) scroller.scrollTop = target;
    };
    const stopRestoring = () => {
      restoring = false;
    };
    const started = performance.now();
    let frames = 0;
    let resizedFrame = 0;
    let resizedAt = started;
    const resized = new ResizeObserver(() => {
      resizedFrame = frames;
      resizedAt = performance.now();
      put();
    });
    resized.observe(scroller);
    if (anchor.current) resized.observe(anchor.current);
    let handle = 0;
    const end = () => {
      cancelAnimationFrame(handle);
      resized.disconnect();
      for (const type of READER_INPUT) scroller.removeEventListener(type, stopRestoring);
      scroller.style.overflowAnchor = anchoring;
      holding.current = null;
    };
    const tick = () => {
      put();
      frames += 1;
      const now = performance.now();
      const settled =
        frames >= KEEP_SCROLL_FRAMES &&
        now - started >= KEEP_SCROLL_MS &&
        frames - resizedFrame >= KEEP_SCROLL_QUIET_FRAMES &&
        now - resizedAt >= KEEP_SCROLL_QUIET_MS;
      if (settled || now - started >= KEEP_SCROLL_MAX_MS) end();
      else handle = requestAnimationFrame(tick);
    };
    for (const type of READER_INPUT) {
      scroller.addEventListener(type, stopRestoring, { passive: true });
    }
    holding.current = end;
    put();
    handle = requestAnimationFrame(tick);
  });
}

export function NavPanes({ children }: { children?: ReactNode }) {
  const api = useNavApi();
  if (api === null) return <>{children}</>;
  return <Panes api={api}>{children}</Panes>;
}

function Panes({ api, children }: { api: NavApi; children?: ReactNode }) {
  const apple = isApplePlatform();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const toggle = useCallback(() => setPaletteOpen((open) => !open), []);
  useEffect(() => {
    const onKey = (event: globalThis.KeyboardEvent) => onPaletteKey(event, apple, toggle);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [apple, toggle]);

  const anchor = useRef<HTMLDivElement>(null);
  const formRef = useRef<'tabs' | 'side-by-side'>('side-by-side');
  const focusAfter = useRef<string | null>(null);
  const closePalette = useCallback((focusTo?: 'main' | 'split') => {
    focusAfter.current = focusTo ? focusIdAfterSwap(formRef.current, focusTo) : null;
    setPaletteOpen(false);
  }, []);
  // Runs after the palette has unmounted and given its focus back, so the swap's target wins.
  useEffect(() => {
    if (paletteOpen || focusAfter.current === null) return;
    document.getElementById(focusAfter.current)?.focus();
    focusAfter.current = null;
  }, [paletteOpen]);
  const { viewport, region } = useRoom(anchor);
  const [wanted, setWanted] = useState<number | null>(null);
  const split = api.state.split;
  const pageDocId = usePageDocId();
  const pageTitle = usePaneTitle(pageDocId);
  const paneTitle = usePaneTitle(split ? currentEntry(split).docId : null);
  const form = split ? splitLayout(viewport, region) : null;
  const tabs = form === 'tabs';
  formRef.current = tabs ? 'tabs' : 'side-by-side';
  const active = api.state.active;
  const activate = (which: 'main' | 'split') => api.update((s) => setActive(s, which));
  useKeepPaneOneScroll(anchor, split !== null, !(tabs && active !== 'main'));

  const first = clampFirstWidth(wanted, region);
  const controls = paneControls(api, 'split');

  const onTabKey = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    const next = event.key === 'ArrowRight' ? 'split' : 'main';
    activate(next);
    document.getElementById(`cx-nav-tab-${next}`)?.focus();
  };

  return (
    <>
      <ShellHeaderItems place="palette">
        <PaletteTrigger apple={apple} open={paletteOpen} onOpen={() => setPaletteOpen(true)} />
      </ShellHeaderItems>
      <div
        ref={anchor}
        className="cx-nav-split"
        data-split={split ? (tabs ? 'tabs' : 'columns') : undefined}
        style={
          split && !tabs
            ? { gridTemplateColumns: `${first}px ${HANDLE_WIDTH}px minmax(0, 1fr)` }
            : undefined
        }
      >
        {split && tabs && (
          <div
            role="tablist"
            aria-label="Panes"
            className="cx-nav-split__tabs"
            data-testid="nav.tabs.list"
          >
            {(['main', 'split'] as const).map((which) => (
              <button
                key={which}
                id={`cx-nav-tab-${which}`}
                type="button"
                role="tab"
                aria-selected={active === which}
                aria-controls={`cx-nav-panel-${which}`}
                tabIndex={active === which ? 0 : -1}
                className="cx-nav-split__tab"
                onClick={() => activate(which)}
                onKeyDown={onTabKey}
              >
                {which === 'main' ? (pageDocId ? pageTitle : 'Page') : paneTitle}
              </button>
            ))}
          </div>
        )}
        <div
          id="cx-nav-panel-main"
          className="cx-nav-split__main"
          tabIndex={-1}
          data-nav-pane="main"
          data-active={split && active === 'main' ? '' : undefined}
          role={tabs ? 'tabpanel' : undefined}
          {...(tabs ? { 'aria-labelledby': 'cx-nav-tab-main' } : {})}
          hidden={tabs && active !== 'main'}
          onFocusCapture={() => split && activate('main')}
          onMouseDownCapture={() => split && activate('main')}
        >
          {children}
        </div>
        {split && !tabs && (
          <SeparatorHandle
            label="Resize split panes"
            value={first}
            min={LAYOUT.splitPaneMin}
            max={firstPaneMax(region)}
            onChange={setWanted}
            className="cx-nav-split__handle"
          />
        )}
        {split && (
          <section
            id="cx-nav-panel-split"
            className="cx-nav-split__pane cx-raised"
            tabIndex={-1}
            data-testid="nav.split.pane"
            data-nav-pane="split"
            data-active={active === 'split' ? '' : undefined}
            role={tabs ? 'tabpanel' : 'region'}
            aria-label={tabs ? undefined : `Split pane: ${paneTitle}`}
            aria-labelledby={tabs ? 'cx-nav-tab-split' : undefined}
            hidden={tabs && active !== 'split'}
            onFocusCapture={() => activate('split')}
            onMouseDownCapture={() => activate('split')}
          >
            <PeekHeader
              title={paneTitle}
              stack={split}
              history
              closeLabel="Close pane"
              {...controls}
              onOpenInSplit={undefined}
            />
            <div className="cx-nav-split__body">
              <LazyPaneDocument
                docId={currentEntry(split).docId}
                blockId={currentEntry(split).blockId}
                source="split"
              />
            </div>
          </section>
        )}
      </div>
      {paletteOpen && <LazyPalette onClose={closePalette} />}
    </>
  );
}
